/**
 * Username utilities for the Communication Hub
 * Provides consistent username formatting and validation across the app
 */

/**
 * Formats a username for display with @ symbol
 * @param {string} username - The username to format
 * @returns {string} - Formatted username with @ symbol
 */
export const formatUsername = (username) => {
  if (!username) return '';
  return `@${username}`;
};

/**
 * Validates username format for communication hub
 * @param {string} username - The username to validate
 * @returns {object} - Validation result with isValid and error message
 */
export const validateUsername = (username) => {
  if (!username) {
    return { isValid: false, error: 'Username is required' };
  }
  
  if (username.length < 3) {
    return { isValid: false, error: 'Username must be at least 3 characters long' };
  }
  
  if (username.length > 30) {
    return { isValid: false, error: 'Username must be less than 30 characters' };
  }
  
  if (!/^[a-z0-9_]+$/.test(username)) {
    return { isValid: false, error: 'Username can only contain lowercase letters, numbers, and underscores' };
  }
  
  return { isValid: true, error: null };
};

/**
 * Sanitizes username input for communication hub
 * @param {string} input - The input to sanitize
 * @returns {string} - Sanitized username
 */
export const sanitizeUsername = (input) => {
  if (!input) return '';
  return input.toLowerCase().replace(/[^a-z0-9_]/g, '');
};

/**
 * Extracts username from @ mention (removes @)
 * @param {string} mention - The mention with @ symbol
 * @returns {string} - Username without @ symbol
 */
export const extractUsername = (mention) => {
  if (!mention) return '';
  return mention.replace(/^@/, '');
};

/**
 * Checks if a string is a valid username mention
 * @param {string} text - The text to check
 * @returns {boolean} - True if it's a valid username mention
 */
export const isValidUsernameMention = (text) => {
  if (!text || !text.startsWith('@')) return false;
  const username = extractUsername(text);
  return validateUsername(username).isValid;
};

/**
 * Generates username suggestions based on name and user type
 * @param {string} name - User's name
 * @param {string} userType - User type (brand/influencer)
 * @returns {array} - Array of username suggestions
 */
export const generateUsernameSuggestions = (name, userType) => {
  if (!name) return [];
  
  const cleanName = sanitizeUsername(name.replace(/\s+/g, '_'));
  const suggestions = [];
  
  // Basic suggestions
  suggestions.push(cleanName);
  suggestions.push(`${cleanName}_${userType}`);
  suggestions.push(`${cleanName}_official`);
  
  // Add numbers for variations
  for (let i = 1; i <= 3; i++) {
    suggestions.push(`${cleanName}${i}`);
    suggestions.push(`${cleanName}_${i}`);
  }
  
  // Type-specific suggestions
  if (userType === 'influencer') {
    suggestions.push(`${cleanName}_creator`);
    suggestions.push(`${cleanName}_content`);
  } else if (userType === 'brand') {
    suggestions.push(`${cleanName}_brand`);
    suggestions.push(`${cleanName}_company`);
  }
  
  // Remove duplicates and filter by validation
  return [...new Set(suggestions)]
    .filter(username => validateUsername(username).isValid)
    .slice(0, 5); // Return top 5 suggestions
};